import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import HomeLayout from '../../asset/layout/HomeLayout';
import SearchBar from '../../components/mui/SearchBar';
import { useDispatch } from 'react-redux';
import { getAllOrders, downloadOrderInvoice } from '../../actions/ordersAuth';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ResponsiveDatePickers from '../../components/mui/PichDate';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import CustomButton from '../../components/mui/CustomButton';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import UsersOrdersTab from '../usersOrders/UsersOrdersTab';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '24px',
      border: '1px solid',
      borderColor: alpha('#11409B', 0.08),
      bgcolor: '#fff',
      boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Card>
);

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    '& fieldset': { borderColor: alpha('#11409B', 0.2) },
    '&:hover fieldset': { borderColor: '#11409B' },
  },
};

const getStatusStyles = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'paid' || s === 'success')
    return { color: '#16a34a', bg: alpha('#16a34a', 0.12) };
  if (s === 'pending_payment' || s === 'pending')
    return { color: '#d97706', bg: alpha('#d97706', 0.12) };
  if (s === 'failed' || s === 'cancelled' || s === 'expired')
    return { color: '#dc2626', bg: alpha('#dc2626', 0.12) };
  return { color: '#64748b', bg: alpha('#64748b', 0.12) };
};

const formatPhone = (phone) => {
  if (!phone) return '—';
  const cleaned = String(phone).replace(/\s/g, '');
  if (cleaned.startsWith('+')) return cleaned;
  if (/^91\d{10}$/.test(cleaned)) return `+${cleaned}`;
  return `+91 ${cleaned}`;
};

const formatAmount = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const StatusChip = ({ status }) => {
  const styles = getStatusStyles(status);
  const label = status ? String(status).replace(/_/g, ' ') : '—';
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: '0.7rem',
        color: styles.color,
        bgcolor: styles.bg,
        border: `1px solid ${alpha(styles.color, 0.2)}`,
        maxWidth: '100%',
      }}
    />
  );
};

export const OderManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [tabValue, setTabValue] = useState(
    tabParam === 'users' || tabParam === 'user-orders' || tabParam === 'users-orders' ? 1 : 0
  );

  const [loading, setLoading] = useState(true);
  const [allOrders, setAllOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDateFrom, setSelectedDateFrom] = useState(null);
  const [selectedDateTo, setSelectedDateTo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentMode, setPaymentMode] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [userId, setUserId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [productCode, setProductCode] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  useEffect(() => {
    if (tabParam === 'users' || tabParam === 'user-orders' || tabParam === 'users-orders') {
      setTabValue(1);
    } else if (tabParam === 'orders') {
      setTabValue(0);
    }
  }, [tabParam]);

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    if (newValue === 1) {
      setSearchParams({ tab: 'user-orders' });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('token')) navigate('/');
  }, [navigate]);

  useEffect(() => {
    if (tabValue === 0) {
      fetchOrders();
    }
  }, [
    searchTerm,
    selectedDateFrom,
    selectedDateTo,
    paymentMode,
    paymentStatus,
    userId,
    orderId,
    transactionId,
    productCode,
    minAmount,
    maxAmount,
    tabValue,
    dispatch,
  ]);

  const fetchOrders = () => {
    setLoading(true);
    dispatch(
      getAllOrders(
        {
          page: 0,
          size: 10000,
          search: searchTerm || undefined,
          userId: userId || undefined,
          orderId: orderId || undefined,
          transactionId: transactionId || undefined,
          productCode: productCode || undefined,
          paymentMode: paymentMode || undefined,
          paymentStatus: paymentStatus || undefined,
          minAmount: minAmount === '' ? undefined : Number(minAmount),
          maxAmount: maxAmount === '' ? undefined : Number(maxAmount),
          createdFrom: selectedDateFrom
            ? new Date(selectedDateFrom).toISOString()
            : undefined,
          createdTo: selectedDateTo
            ? new Date(selectedDateTo).toISOString()
            : undefined,
        },
        (data) => {
          const orderContent =
            data?.content ||
            data?.data?.content ||
            data?.data?.data?.content ||
            data?.data?.data?.Orders ||
            data?.data?.Orders ||
            [];
          setAllOrders(Array.isArray(orderContent) ? orderContent : []);
          setLoading(false);
        },
        () => setLoading(false)
      )
    );
  };

  const stats = useMemo(() => {
    const total = allOrders.length;
    const paid = allOrders.filter((o) => {
      const s = (o.paymentStatus || o.resultType || '').toLowerCase();
      return s === 'paid' || s === 'success';
    }).length;
    const pending = allOrders.filter((o) => {
      const s = (o.paymentStatus || o.resultType || '').toLowerCase();
      return s === 'pending_payment' || s === 'pending';
    }).length;
    const failed = allOrders.filter((o) => {
      const s = (o.paymentStatus || o.resultType || '').toLowerCase();
      return s === 'failed' || s === 'cancelled';
    }).length;
    return { total, paid, pending, failed };
  }, [allOrders]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const exportToExcel = () => {
    const header = [
      'Transaction ID',
      'Order ID',
      'Product',
      'Phone',
      'Status',
      'Amount',
      'Qty',
      'Date',
      'Brand Code',
    ];
    const dataToExport = allOrders.map((order) => [
      order?.transactionId || order?.externalOrderIdPayment || '—',
      order?.id || order?.orderId || order?.externalOrderIdVoucher || '—',
      order?.productCode || order?.pullVouchers?.[0]?.ProductGuid || '—',
      order?.user?.phone || '—',
      order?.paymentStatus || order?.resultType || '—',
      order?.totalAmount ?? order?.amount ?? order?.denomination ?? '—',
      order?.quantity ?? '—',
      order?.createdAt ?? '—',
      order?.brandProductCode || order?.productCode || '—',
    ]);
    dataToExport.unshift(header);
    const worksheet = XLSX.utils.aoa_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      'Orders_data.xlsx'
    );
  };

  const totalPages = Math.ceil(allOrders.length / countPerPage) || 1;
  const currentOrders = allOrders.slice(
    (currentPage - 1) * countPerPage,
    currentPage * countPerPage
  );

  const handleSearchClear = () => {
    setSearchTerm('');
    setSelectedDateFrom(null);
    setSelectedDateTo(null);
    setPaymentMode('');
    setPaymentStatus('');
    setUserId('');
    setOrderId('');
    setTransactionId('');
    setProductCode('');
    setMinAmount('');
    setMaxAmount('');
    setCurrentPage(1);
  };

  const handlePhoneClick = (order) => {
    setSelectedOrder(order);
    setModalOpen(true);
  };

  const [downloadingOrderId, setDownloadingOrderId] = useState(null);

  const parseBlobError = async (err) => {
    if (err?.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const json = JSON.parse(text);
        return json?.message || json?.error || 'Failed to download invoice';
      } catch {
        return 'Failed to download invoice';
      }
    }
    return err?.response?.data?.message || err?.message || 'Failed to download invoice';
  };

  const handleDownloadInvoice = (order) => {
    const orderId =
      order?.id ||
      order?.orderId ||
      order?._id ||
      order?.externalOrderIdVoucher;

    if (!orderId) {
      enqueueSnackbar('Order ID is missing for this order', { variant: 'error' });
      return;
    }

    setDownloadingOrderId(orderId);
    dispatch(
      downloadOrderInvoice(
        orderId,
        (response) => {
          try {
            const disposition =
              response?.headers?.['content-disposition'] ||
              response?.headers?.['Content-Disposition'];
            let filename = `invoice_${orderId}.pdf`;
            if (disposition) {
              const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (match?.[1]) {
                filename = match[1].replace(/['"]/g, '').trim();
              }
            }
            const contentType =
              response?.headers?.['content-type'] ||
              response?.headers?.['Content-Type'] ||
              'application/pdf';
            const blob = new Blob([response.data], { type: contentType });
            saveAs(blob, filename);
            enqueueSnackbar('Invoice downloaded successfully', { variant: 'success' });
          } catch (e) {
            console.error('Failed to save invoice file:', e);
            enqueueSnackbar('Failed to save downloaded invoice', { variant: 'error' });
          } finally {
            setDownloadingOrderId(null);
          }
        },
        async (err) => {
          const errMsg = await parseBlobError(err);
          enqueueSnackbar(errMsg, { variant: 'error' });
          setDownloadingOrderId(null);
        }
      )
    );
  };

  const StatMini = ({ label, value, color }) => (
    <Box
      sx={{
        p: 2,
        borderRadius: '16px',
        bgcolor: alpha(color, 0.08),
        border: `1px solid ${alpha(color, 0.15)}`,
        textAlign: 'center',
      }}
    >
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: '#0f172a' }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <HomeLayout>
      <Box
        sx={{
          minHeight: '100%',
          background:
            'linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)',
          mx: -3,
          mt: -3,
          px: 3,
          pt: 3,
          pb: 4,
        }}
      >
        {/* Hero */}
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              borderRadius: '24px',
              background:
                'linear-gradient(135deg, #0c3483 0%, #11409B 40%, #1e40af 70%, #2563eb 100%)',
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(17, 64, 155, 0.35)',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <ShoppingCartOutlinedIcon />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, letterSpacing: '0.1em', opacity: 0.85 }}
                  >
                    {tabValue === 0 ? 'ORDER MANAGEMENT' : "USER'S ORDERS"}
                  </Typography>
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
                  {tabValue === 0 ? 'Orders dashboard' : "User's orders & transactions"}
                </Typography>
                <Typography sx={{ mt: 1, opacity: 0.82, maxWidth: 520 }}>
                  {tabValue === 0
                    ? 'View, filter, and export all orders. Click a phone number to see customer details.'
                    : "View paginated users with order metrics, real-time status breakdowns (Success, Pending, Failed), and detailed orders."}
                </Typography>
              </Box>
              {tabValue === 0 && (
                <CustomButton
                  variant="contained"
                  onClick={exportToExcel}
                  startIcon={<FileDownloadOutlinedIcon />}
                  sx={{
                    height: '44px',
                    borderRadius: '12px',
                    fontWeight: 600,
                    textTransform: 'none',
                    bgcolor: alpha('#fff', 0.15),
                    border: `1px solid ${alpha('#fff', 0.25)}`,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: alpha('#fff', 0.22) },
                  }}
                >
                  Export orders
                </CustomButton>
              )}
            </Stack>
          </Box>
        </motion.div>

        {/* Navigation Tabs */}
        <Box
          sx={{
            mb: 3,
            bgcolor: '#fff',
            borderRadius: '16px',
            border: `1px solid ${alpha('#11409B', 0.1)}`,
            boxShadow: '0 4px 24px rgba(15, 23, 42, 0.05)',
            px: 1,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="order management tabs"
            sx={{
              minHeight: 48,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                color: '#64748b',
                minHeight: 48,
              },
              '& .Mui-selected': { color: '#11409B' },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                bgcolor: '#11409B',
              },
            }}
          >
            <Tab label="All Orders" />
            <Tab label="User's Orders" />
          </Tabs>
        </Box>

        {tabValue === 0 ? (
          <>
            {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total', value: stats.total, color: '#4f46e5' },
            { label: 'Paid', value: stats.paid, color: '#16a34a' },
            { label: 'Pending', value: stats.pending, color: '#d97706' },
            { label: 'Failed', value: stats.failed, color: '#dc2626' },
          ].map((s, i) => (
            <Grid item xs={6} md={3} key={s.label}>
              <motion.div custom={i} initial="hidden" animate="visible" variants={fadeUp}>
                <StatMini {...s} />
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Filters */}
        <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
          <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <FilterListOutlinedIcon sx={{ color: '#11409B' }} />
              <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>Filters</Typography>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <SearchBar onSearch={handleSearch} searchTerm={searchTerm} height="2.75rem" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField fullWidth size="small" label="User ID" value={userId} onChange={(e) => { setUserId(e.target.value); setCurrentPage(1); }} sx={fieldSx} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField fullWidth size="small" label="Order ID" value={orderId} onChange={(e) => { setOrderId(e.target.value); setCurrentPage(1); }} sx={fieldSx} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField fullWidth size="small" label="Transaction ID" value={transactionId} onChange={(e) => { setTransactionId(e.target.value); setCurrentPage(1); }} sx={fieldSx} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField fullWidth size="small" label="Product code" value={productCode} onChange={(e) => { setProductCode(e.target.value); setCurrentPage(1); }} sx={fieldSx} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>Payment mode</InputLabel>
                  <Select value={paymentMode} label="Payment mode" onChange={(e) => { setPaymentMode(e.target.value); setCurrentPage(1); }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="WALLET">Wallet</MenuItem>
                    <MenuItem value="RAZORPAY">Razorpay</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>Status</InputLabel>
                  <Select value={paymentStatus} label="Status" onChange={(e) => { setPaymentStatus(e.target.value); setCurrentPage(1); }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="PENDING_PAYMENT">Pending</MenuItem>
                    <MenuItem value="PAID">Paid</MenuItem>
                    <MenuItem value="FAILED">Failed</MenuItem>
                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={1.5}>
                <TextField fullWidth size="small" type="number" label="Min ₹" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} sx={fieldSx} />
              </Grid>
              <Grid item xs={6} sm={3} md={1.5}>
                <TextField fullWidth size="small" type="number" label="Max ₹" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} sx={fieldSx} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <ResponsiveDatePickers selectedDate={selectedDateFrom} setSelectedDate={setSelectedDateFrom} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <ResponsiveDatePickers selectedDate={selectedDateTo} setSelectedDate={setSelectedDateTo} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <CustomButton
                  variant="outlined"
                  onClick={handleSearchClear}
                  fullWidth
                  sx={{
                    height: '2.75rem',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: alpha('#11409B', 0.25),
                    color: '#11409B',
                  }}
                >
                  Clear
                </CustomButton>
              </Grid>
            </Grid>
          </PanelCard>
        </motion.div>

        {/* Table */}
        <motion.div custom={5} initial="hidden" animate="visible" variants={fadeUp}>
          {loading ? (
            <PanelCard>
              <Stack alignItems="center" py={10}>
                <CircularProgress sx={{ color: '#11409B' }} />
                <Typography sx={{ mt: 2, color: '#64748b' }}>Loading orders…</Typography>
              </Stack>
            </PanelCard>
          ) : currentOrders.length === 0 ? (
            <PanelCard>
              <Stack alignItems="center" py={8}>
                <ShoppingCartOutlinedIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>No orders found</Typography>
                <Typography sx={{ color: '#64748b', mt: 1 }}>Adjust filters and try again.</Typography>
              </Stack>
            </PanelCard>
          ) : (
            <TableContainer
              component={PanelCard}
              sx={{ overflowX: 'auto' }}
            >
              <Table sx={{ minWidth: 1100 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#11409B', 0.04) }}>
                    {[
                      'Transaction ID',
                      'Order ID',
                      'Product',
                      'Contact',
                      'Status',
                      'Amount',
                      'Qty',
                      'Date',
                      'Brand',
                      'Invoice',
                    ].map((col) => (
                      <TableCell
                        key={col}
                        align={col === 'Invoice' ? 'right' : 'left'}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          color: '#11409B',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          py: 2,
                          whiteSpace: 'nowrap',
                          ...(col === 'Brand' && { display: { xs: 'none', lg: 'table-cell' } }),
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentOrders.map((row) => {
                    const rowKey = row?.id || row?.orderId || row?.transactionId;
                    const status = row?.paymentStatus || row?.resultType;
                    const ordKey = row?.id || row?.orderId || row?._id || row?.externalOrderIdVoucher;
                    const isDownloading = downloadingOrderId === ordKey;

                    return (
                      <TableRow
                        key={rowKey}
                        sx={{
                          '&:hover': { bgcolor: alpha('#11409B', 0.03) },
                          '& td': { borderColor: alpha('#11409B', 0.06), py: 1.75 },
                        }}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#475569' }}
                          >
                            {row?.transactionId || row?.externalOrderIdPayment || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#475569' }}
                          >
                            {row?.id || row?.orderId || row?.externalOrderIdVoucher || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                            {row?.productCode || row?.pullVouchers?.[0]?.ProductGuid || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            onClick={() => row?.user?.phone && handlePhoneClick(row)}
                            sx={{
                              cursor: row?.user?.phone ? 'pointer' : 'default',
                              '&:hover': row?.user?.phone ? { opacity: 0.85 } : {},
                            }}
                          >
                            <Stack spacing={0.5} sx={{ minWidth: 140 }}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <PhoneOutlinedIcon sx={{ fontSize: 14, color: '#11409B' }} />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: row?.user?.phone ? '#11409B' : '#94a3b8',
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  {formatPhone(row?.user?.phone)}
                                </Typography>
                              </Stack>
                              {row?.user?.email && (
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <EmailOutlinedIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                  <Typography
                                    variant="caption"
                                    sx={{ color: '#64748b', wordBreak: 'break-all' }}
                                  >
                                    {row.user.email}
                                  </Typography>
                                </Stack>
                              )}
                            </Stack>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <StatusChip status={status} />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                            {formatAmount(row?.totalAmount ?? row?.amount ?? row?.denomination)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row?.quantity ?? '—'}
                            size="small"
                            sx={{ fontWeight: 600, bgcolor: alpha('#11409B', 0.08) }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                            {row?.createdAt
                              ? dayjs(row.createdAt).format('DD MMM YYYY')
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography variant="body2" sx={{ color: '#475569' }}>
                            {row?.brandProductCode || row?.productCode || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Download invoice">
                            <span>
                              <CustomButton
                                size="small"
                                variant="outlined"
                                disabled={isDownloading}
                                onClick={() => handleDownloadInvoice(row)}
                                startIcon={
                                  isDownloading ? (
                                    <CircularProgress size={12} sx={{ color: '#11409B' }} />
                                  ) : (
                                    <ReceiptLongOutlinedIcon sx={{ fontSize: 14 }} />
                                  )
                                }
                                sx={{
                                  borderRadius: '8px',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: '0.72rem',
                                  py: 0.25,
                                  px: 1,
                                  borderColor: alpha('#11409B', 0.25),
                                  color: '#11409B',
                                  '&:hover': {
                                    bgcolor: alpha('#11409B', 0.06),
                                    borderColor: '#11409B',
                                  },
                                }}
                              >
                                {isDownloading ? 'Downloading…' : 'Invoice'}
                              </CustomButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </motion.div>

        {/* Pagination */}
        {!loading && allOrders.length > 0 && (
          <motion.div custom={6} initial="hidden" animate="visible" variants={fadeUp}>
            <PanelCard sx={{ mt: 3, p: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography sx={{ color: '#64748b', fontWeight: 500 }}>
                  Showing {(currentPage - 1) * countPerPage + 1}–
                  {Math.min(currentPage * countPerPage, allOrders.length)} of {allOrders.length}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>Rows</Typography>
                  <Box
                    component="select"
                    value={countPerPage}
                    onChange={(e) => {
                      setCountPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    sx={{
                      border: `1px solid ${alpha('#11409B', 0.2)}`,
                      borderRadius: '10px',
                      px: 1.5,
                      py: 0.75,
                      bgcolor: '#fff',
                    }}
                  >
                    {[10, 25, 30, 50].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </Box>
                  <IconButton
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    sx={{ border: '1px solid', borderColor: alpha('#11409B', 0.15), borderRadius: '12px' }}
                  >
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                  <Typography sx={{ fontWeight: 600 }}>
                    {currentPage} / {totalPages}
                  </Typography>
                  <IconButton
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage >= totalPages}
                    sx={{ border: '1px solid', borderColor: alpha('#11409B', 0.15), borderRadius: '12px' }}
                  >
                    <KeyboardArrowRightIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </PanelCard>
          </motion.div>
        )}

        {/* Customer modal */}
        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '92%', sm: 420 },
              maxHeight: '90vh',
              overflow: 'auto',
              outline: 'none',
            }}
          >
            <PanelCard sx={{ p: 3, position: 'relative' }}>
              <IconButton
                onClick={() => setModalOpen(false)}
                sx={{ position: 'absolute', top: 12, right: 12 }}
              >
                <CloseIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 800, textAlign: 'center', mb: 3 }}>
                Customer details
              </Typography>
              {selectedOrder?.user && (
                <Stack alignItems="center" spacing={2}>
                  <Avatar
                    src={selectedOrder.user.pictureUrl}
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: 'linear-gradient(135deg, #11409B, #2563eb)',
                      fontSize: '1.5rem',
                      fontWeight: 700,
                    }}
                  >
                    {(selectedOrder.user.username?.[0] ||
                      selectedOrder.user.name?.[0] ||
                      '?').toUpperCase()}
                  </Avatar>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    {selectedOrder.user.name || selectedOrder.user.username || '—'}
                  </Typography>
                  {[
                    { label: 'Phone', value: formatPhone(selectedOrder.user.phone) },
                    { label: 'Email', value: selectedOrder.user.email || '—' },
                    {
                      label: 'Reward points',
                      value: Number.isFinite(Number(selectedOrder.user.rewardPoint))
                        ? Number(selectedOrder.user.rewardPoint).toFixed(2)
                        : '0.00',
                    },
                  ].map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        width: '100%',
                        p: 1.5,
                        borderRadius: '12px',
                        bgcolor: alpha('#11409B', 0.04),
                        border: `1px solid ${alpha('#11409B', 0.08)}`,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontWeight: 600, color: '#0f172a' }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </PanelCard>
          </Box>
        </Modal>
          </>
        ) : (
          <UsersOrdersTab />
        )}
      </Box>
    </HomeLayout>
  );
};
